/**
 * In-memory cache of manufacturing.wireless_cellular_sku_rule (cellular wireless finished SKUs).
 * Refreshed at API and scheduler boot. Sync matchers read the cache so call sites stay sync.
 *
 * To recognize a new wireless product: insert an active canonical_prefix row (and restart or
 * call refreshWirelessCellularSkuRulesFromDb). Do not hardcode new prefixes in TypeScript.
 */

import type { Pool } from "pg";

/** Seeded defaults matching migration 604; used until DB refresh succeeds. */
export const FALLBACK_WIRELESS_CELLULAR_CANONICAL_PREFIXES = ["FI-5A-W", "DM-1"] as const;

let canonicalPrefixes: string[] = [...FALLBACK_WIRELESS_CELLULAR_CANONICAL_PREFIXES];

export async function refreshWirelessCellularSkuRulesFromDb(pool: Pool): Promise<void> {
  const { createLogger } = await import("../utils/logger.js");
  const logger = createLogger("wireless-cellular-sku-rules");
  try {
    const result = await pool.query<{ match_value: string }>(
      `select upper(trim(manufacturing.wireless_cellular_sku_rule.match_value)) as match_value
         from manufacturing.wireless_cellular_sku_rule
        where manufacturing.wireless_cellular_sku_rule.active
          and manufacturing.wireless_cellular_sku_rule.match_kind = 'canonical_prefix'
          and coalesce(trim(manufacturing.wireless_cellular_sku_rule.match_value), '') <> ''
        order by char_length(trim(manufacturing.wireless_cellular_sku_rule.match_value)) desc,
                 manufacturing.wireless_cellular_sku_rule.match_value`,
    );
    if (result.rowCount === 0) {
      logger.warn(
        "manufacturing.wireless_cellular_sku_rule has no active rows; using fallback prefixes.",
      );
      canonicalPrefixes = [...FALLBACK_WIRELESS_CELLULAR_CANONICAL_PREFIXES];
      return;
    }
    const next: string[] = [];
    const seen = new Set<string>();
    for (const row of result.rows) {
      const value = row.match_value.trim().toUpperCase();
      if (value === "" || seen.has(value)) {
        continue;
      }
      seen.add(value);
      next.push(value);
    }
    canonicalPrefixes = next;
    logger.info({ count: next.length, prefixes: next }, "Loaded wireless cellular SKU rules from database.");
  } catch (err) {
    logger.error(
      { err: err instanceof Error ? err.message : String(err) },
      "Failed to load manufacturing.wireless_cellular_sku_rule; keeping in-memory prefixes from last successful load or fallback.",
    );
  }
}

/** Read-only snapshot for APIs and tests. */
export function getWirelessCellularCanonicalPrefixesSnapshot(): readonly string[] {
  return canonicalPrefixes;
}

/** Tests only: replace the in-memory prefix list (proves extensibility without code edits). */
export function setWirelessCellularCanonicalPrefixesForTests(prefixes: readonly string[]): void {
  canonicalPrefixes = prefixes.map((prefix) => prefix.trim().toUpperCase()).filter((prefix) => prefix !== "");
}

export function resetWirelessCellularCanonicalPrefixesForTests(): void {
  canonicalPrefixes = [...FALLBACK_WIRELESS_CELLULAR_CANONICAL_PREFIXES];
}

/** Trusted internal SQL expression predicate (uses DB function; rules live in the table). */
export function sqlSkuMatchesWirelessCellularFamily(skuExpression: string): string {
  return `manufacturing.is_wireless_cellular_sku(${skuExpression})`;
}
